import { useState } from 'react';

interface FAQItem {
  id: number;
  question: string;
  answer: {
    main: string;
    example?: { label: string; text: string };
    tip?: string;
  };
}

export const costFAQs: FAQItem[] = [
  {
    id: 1,
    question: 'Why does 0.0 pip spread still cost money?',
    answer: {
      main: 'Raw/ECN accounts show 0.0 pip spread but charge a separate commission — typically $3–$7 per side per lot. A 0.2 pip spread with $7 round-turn commission costs more than a 1.0 pip "commission-free" spread.',
      example: { label: 'Real numbers', text: '0.2p ECN: $2 spread + $7 commission = $9/lot. 1.0p Standard: $10 spread + $0 commission = $10/lot. The "free" account costs more — but only if you don\'t trade enough volume to justify ECN minimums.' },
      tip: 'Always add spread + commission together when comparing accounts. The 0.0 rate also only applies during peak liquidity — spreads can widen significantly during rollover and news.',
    },
  },
  {
    id: 2,
    question: 'How do I calculate the full cost of a trade?',
    answer: {
      main: 'Add three layers: (1) entry cost — spread in pips × pip value × lots, (2) execution cost — commission per side × 2 × lots, (3) holding cost — swap × days held × pip value × lots. Then add any currency conversion markup if your account currency differs from the pair\'s base.',
      example: { label: 'Worked example', text: '1-lot EUR/USD, 0.6p spread ($6) + $3.50/side commission ($7) + 3 days at 0.3p/day swap ($9) = $22 total. This is your break-even before you make a single pip of profit.' },
      tip: 'Factor in potential slippage during volatile periods. If entering during NFP, add 2–5 pips to your estimated cost.',
    },
  },
  {
    id: 3,
    question: 'Are commission-free accounts actually cheaper?',
    answer: {
      main: 'Not necessarily — they build the fee into a wider spread. A 1.2 pip Standard spread costs ~$12/lot, comparable to an ECN with 0.2p ($2) + $7 commission ($9 total). For active scalpers, ECN is usually cheaper due to narrower spreads.',
      example: { label: 'The math', text: 'Scapler at 15 RT/day: ECN 0.2p+$7 = $135/day → $2,970/month. Standard 1.0p "free" = $150/day → $3,300/month. ECN saves $330/month — over $3,900/year.' },
      tip: 'Calculate the all-in cost for your specific volume and style. Don\'t trust the marketing label — "commission-free" is a pricing strategy, not a discount.',
    },
  },
  {
    id: 4,
    question: 'When do swap charges apply and why Wednesday?',
    answer: {
      main: 'Swap applies when a position stays open past 5 PM New York (10 PM GMT). The rate depends on the interest rate differential between the two currencies: long the higher-rate currency earns positive swap, long the lower-rate currency pays negative swap.',
      example: { label: 'Wednesday triple swap', text: 'Forex settles T+2, so a trade held through Wednesday evening covers Saturday + Sunday + Monday = triple swap. Same logic: some brokers charge double on Thursday for metals and indices due to different settlement conventions.' },
      tip: 'Swap rates vary by broker, pair, and position direction. Check your broker\'s swap table before holding overnight — some pairs can cost 5–10 pips/day in negative swap.',
    },
  },
  {
    id: 5,
    question: 'Why did my trade fill at a worse price?',
    answer: {
      main: 'That\'s slippage — the gap between your requested price and actual execution. During high-impact news, liquidity thins and spreads widen, causing market orders to fill several pips away. Exotic pairs and rollover periods have the same issue due to inherently low liquidity.',
      example: { label: 'Common scenario', text: 'You click Buy on GBP/USD at 1.2650 during NFP. The market has already moved to 1.2653 by the time your order reaches the broker — that\'s 3 pips of slippage, or $30 on a standard lot. One bad fill can cost more than a week of normal spreads.' },
      tip: 'Use limit orders for news-event entries. You might miss the trade, but you won\'t overpay. For major pairs, trade during London/New York overlap for tightest execution.',
    },
  },
  {
    id: 6,
    question: 'What hidden costs should I watch for?',
    answer: {
      main: 'Five costs that never appear on the trade ticket: currency conversion markup (~0.3–0.5% per P&L conversion), withdrawal fees ($20–$40 per wire), inactivity penalties (monthly after 3–6 months idle), spread widening outside peak hours, and negative balance exposure in jurisdictions without mandatory protection.',
      example: { label: 'Silent drain', text: 'EUR account trading USD/JPY: every profit conversion costs 0.5%. Make 100 trades/month at breakeven after spreads/commissions, and that 0.5% conversion markup on round-turns alone could cost you $200–$500/month in invisible fees.' },
      tip: 'Open accounts in the same currency as your most-traded pairs. If you trade USD pairs, use a USD-denominated account to avoid conversion costs entirely.',
    },
  },
  {
    id: 7,
    question: 'Which trading style pays the most in costs?',
    answer: {
      main: 'It depends on your metric. Per-trade, scalpers pay the least individually but the most in total due to volume — 20 trades/day at $6 spread + $7 commission = $260/day. Per-position, swing traders can pay the most in swap — a 30-day hold at 0.6 pips/day costs 18 pips in swap alone.',
      example: { label: 'Monthly comparison', text: 'Scapler (15 RT/day): ~$2,860/month driven by commission. Day trader (3 trades/day): ~$990/month driven by spread. Swing trader (3 positions/month): ~$495/month driven by swap. Same market, same lot size — 5.8× difference in monthly costs.' },
      tip: 'Identify which cost dominates YOUR style: spread for scalpers, slippage for news traders, swap for swing traders. Optimize the right thing — cutting spread from 1.0p to 0.6p saves a scalper $1,000/month but saves a swing trader only $12.',
    },
  },
];

export default function CostFAQSection() {
  const [activeId, setActiveId] = useState<number>(1);
  const activeFaq = costFAQs.find((f) => f.id === activeId) || costFAQs[0];

  const toggleMobileFaq = (id: number) => {
    setActiveId((prev) => (prev === id ? 0 : id));
  };

  return (
    <section className="w-full bg-background-100">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-[64px] md:py-[88px]">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-6 mb-10 md:mb-14">
          <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-[#101828] tracking-[-0.015em]">
            The stuff every trader wonders but rarely asks.
          </h2>
        </div>

        {/* ═══════════ DESKTOP: Split panel layout ═══════════ */}
        <div className="hidden lg:flex flex-row gap-0">
          {/* LEFT: Question list */}
          <div className="w-[400px] flex-shrink-0 bg-white border border-[#E6EAF0] rounded-2xl rounded-r-none overflow-hidden">
            {costFAQs.map((faq, idx) => (
              <button
                key={faq.id}
                onClick={() => setActiveId(faq.id)}
                className={`w-full text-left px-5 py-4 flex items-start gap-3 transition-colors cursor-pointer ${
                  idx < costFAQs.length - 1 ? 'border-b border-[#F2F4F8]' : ''
                } ${
                  activeId === faq.id
                    ? 'bg-[#F6F8FB]'
                    : 'hover:bg-[#FAFBFC]'
                }`}
              >
                <span
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold mt-0.5 ${
                    activeId === faq.id
                      ? 'bg-[#0B1F3A] text-white'
                      : 'bg-[#F2F4F8] text-[#94A3B8]'
                  }`}
                >
                  {String(faq.id).padStart(2, '0')}
                </span>
                <span className={`text-[14px] font-semibold leading-[20px] ${
                  activeId === faq.id ? 'text-[#101828]' : 'text-[#475467]'
                }`}>
                  {faq.question}
                </span>
                {activeId === faq.id && (
                  <i className="ri-arrow-right-s-line text-[#667085] ml-auto flex-shrink-0 mt-0.5"></i>
                )}
              </button>
            ))}
          </div>

          {/* RIGHT: Answer panel */}
          <div className="flex-1 bg-white border border-[#E6EAF0] border-l-0 rounded-2xl rounded-l-none p-8 lg:p-10">
            <div className="max-w-[560px]">
              <div className="flex items-center gap-2 mb-6">
                <span className="text-[12px] font-bold text-[#94A3B8] bg-[#F6F8FB] px-2.5 py-1 rounded-full">
                  Q{String(activeFaq.id).padStart(2, '0')}
                </span>
              </div>
              <h3 className="text-[24px] font-[750] leading-[32px] text-[#101828] mb-6">
                {activeFaq.question}
              </h3>
              <p className="text-[16px] leading-[28px] text-[#475467] mb-6">
                {activeFaq.answer.main}
              </p>
              {activeFaq.answer.example && (
                <div className="mb-5 rounded-xl border border-[#E6EAF0] bg-[#F6F8FB] overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#E6EAF0] bg-white">
                    <i className="ri-calculator-line text-[13px] text-[#667085]"></i>
                    <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">{activeFaq.answer.example.label}</span>
                  </div>
                  <p className="px-4 py-3 text-[14px] leading-[22px] text-[#475467]">
                    {activeFaq.answer.example.text}
                  </p>
                </div>
              )}
              {activeFaq.answer.tip && (
                <div className="flex items-start gap-2.5 p-4 rounded-xl" style={{ backgroundColor: '#EEF4FF' }}>
                  <i className="ri-lightbulb-flash-line text-[14px] mt-[2px] flex-shrink-0" style={{ color: '#2563EB' }}></i>
                  <p className="text-[13.5px] leading-[21px] text-[#475467]">
                    {activeFaq.answer.tip}
                  </p>
                </div>
              )}
              <div className="mt-10 pt-6 border-t border-[#F2F4F8] flex items-center gap-2">
                <i className="ri-information-line text-sm text-[#94A3B8]"></i>
                <span className="text-[12px] text-[#94A3B8]">
                  These answers reflect general market conditions. Always verify with your broker.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ MOBILE: Accordion layout ═══════════ */}
        <div className="lg:hidden flex flex-col gap-1.5">
          {costFAQs.map((faq) => {
            const isOpen = activeId === faq.id;
            return (
              <div
                key={faq.id}
                className="bg-white border border-[#E6EAF0] rounded-xl overflow-hidden transition-shadow duration-200"
              >
                {/* Question row — tappable */}
                <button
                  onClick={() => toggleMobileFaq(faq.id)}
                  className="w-full text-left px-4 py-3.5 flex items-center gap-3 cursor-pointer"
                >
                  <span
                    className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                      isOpen
                        ? 'bg-[#0B1F3A] text-white'
                        : 'bg-[#F2F4F8] text-[#94A3B8]'
                    }`}
                  >
                    {String(faq.id).padStart(2, '0')}
                  </span>
                  <span
                    className={`flex-1 text-[14px] font-semibold leading-[20px] pr-2 ${
                      isOpen ? 'text-[#101828]' : 'text-[#475467]'
                    }`}
                  >
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-base text-[#94A3B8] flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>

                {/* Answer body — animated expand/collapse */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-4 pb-4">
                      <div className="border-t border-[#F2F4F8] pt-4">
                        {/* Main answer */}
                        <p className="text-[14px] leading-[24px] text-[#475467] mb-4">
                          {faq.answer.main}
                        </p>

                        {/* Example callout */}
                        {faq.answer.example && (
                          <div className="mb-4 rounded-lg border border-[#E6EAF0] bg-[#F6F8FB] overflow-hidden">
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E6EAF0] bg-white">
                              <i className="ri-calculator-line text-[12px] text-[#667085]"></i>
                              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#94A3B8]">
                                {faq.answer.example.label}
                              </span>
                            </div>
                            <p className="px-3 py-2.5 text-[13px] leading-[20px] text-[#475467]">
                              {faq.answer.example.text}
                            </p>
                          </div>
                        )}

                        {/* Tip */}
                        {faq.answer.tip && (
                          <div
                            className="flex items-start gap-2 p-3 rounded-lg"
                            style={{ backgroundColor: '#EEF4FF' }}
                          >
                            <i
                              className="ri-lightbulb-flash-line text-[13px] mt-[2px] flex-shrink-0"
                              style={{ color: '#2563EB' }}
                            ></i>
                            <p className="text-[12.5px] leading-[20px] text-[#475467]">
                              {faq.answer.tip}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Bottom note */}
          <div className="mt-3 pt-4 flex items-center gap-2 px-1">
            <i className="ri-information-line text-xs text-[#94A3B8]"></i>
            <span className="text-[11px] text-[#94A3B8]">
              These answers reflect general market conditions. Always verify with your broker.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}